/**
 * Client detail - the configuration every future billing cycle will use.
 *
 * Read-only by design: billing happens in the console, and changing a client's
 * setup is an explicit Edit. Layout follows the design source
 * (B2BClientMaster → `ClientDetails`): a stat strip, then the configuration as
 * a two-column detail grid, then the room inventory.
 *
 * This page deliberately does NOT call the cycle endpoint. That call opens the
 * client's first billing cycle as a side effect, which is right when an operator
 * walks into the console to start work and wrong when someone merely opens a
 * record to check a phone number.
 */
import { useCallback, useEffect, useState } from 'react'
import { CalendarDays, Check, DoorOpen, FileText, Pencil } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'

import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import { withServerTimeout } from '../../components/common/loadErrorUtils'
import { useColors } from '../../hooks/useColors'
import {
  Card,
  CardTitle,
  Chip,
  PageHeader,
  PillButton,
  StatCard,
  StatRow,
} from '../../features/b2b/components/primitives'
import ClientStatusBadge from '../../features/b2b/components/ClientStatusBadge'
import ClientFormModal from './ClientFormModal'
import { formatMoney } from '../../features/b2b/format'
import { b2bApi } from '../../services/b2bApi'
import type { B2bClientRecord, B2bRoomRecord } from '../../services/b2bApi'

const TYPE_LABELS: Record<string, string> = {
  pg: 'PG',
  hostel: 'Hostel',
  society: 'Society',
  corporate: 'Corporate',
  other: 'Other',
}

const CYCLE_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

interface OpenCycle {
  id: string
  label: string
  total_amount: number
  rooms_billed: number
}

/** One label/value row of the design's configuration grid. */
function DetailRow({ label, value }: { label: string; value: string }) {
  const colors = useColors()

  return (
    <div style={{ borderBottom: `1px solid ${colors.surface}`, padding: '12px 0' }}>
      <div
        className="font-body"
        style={{
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.9px',
          textTransform: 'uppercase',
          color: colors.textMuted,
          marginBottom: '4px',
        }}
      >
        {label}
      </div>
      <div className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
        {value}
      </div>
    </div>
  )
}

export default function B2bClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const colors = useColors()

  const [client, setClient] = useState<B2bClientRecord | null>(null)
  const [rooms, setRooms] = useState<B2bRoomRecord[]>([])
  const [roomCount, setRoomCount] = useState(0)
  const [openCycle, setOpenCycle] = useState<OpenCycle | null>(null)
  const [rateCardSummary, setRateCardSummary] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notFound, setNotFound] = useState(false)
  const [editing, setEditing] = useState(false)
  const [outstanding, setOutstanding] = useState<{ amount: number; invoice_count: number } | null>(null)

  const load = useCallback(async () => {
    if (!clientId) return
    setLoading(true)
    setError('')
    setNotFound(false)
    try {
      const [detail, rates] = await Promise.all([
        withServerTimeout(b2bApi.getClient(clientId)),
        // A missing rate card must not blank the whole page.
        b2bApi.getRates(clientId).catch(() => null),
      ])
      setClient(detail.client)
      setRooms(detail.rooms)
      setRoomCount(detail.room_count)
      setOpenCycle(detail.open_cycle)
      setOutstanding(detail.outstanding)
      setRateCardSummary(
        rates ? `₹${rates.rate_card.washIronPerKg}/kg · ${Object.keys(rates.rate_card.itemPrices).length} item rates` : '-',
      )
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        setNotFound(true)
      } else {
        setError(
          (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
            ?.message ||
            (err as Error)?.message ||
            'Could not load this client.',
        )
      }
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return (
      <div>
        <PageHeader title="Loading client…" />
        <StatRow>
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="rapidry-pulse"
              style={{ flex: 1, minWidth: '170px', height: '92px', borderRadius: '16px', background: colors.surface }}
            />
          ))}
        </StatRow>
        <Card section>
          <div
            className="rapidry-pulse"
            style={{ height: '220px', borderRadius: '12px', background: colors.surface }}
          />
        </Card>
      </div>
    )
  }

  if (notFound || (!client && !error)) {
    return (
      <div>
        <PageHeader title="Client not found" subtitle="This B2B client no longer exists." />
        <Card section>
          <EmptyState
            title="Nothing to show"
            description="The client may have been removed. Go back to the client list."
          />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PillButton onClick={() => navigate('/b2b/clients')}>Back to Clients</PillButton>
          </div>
        </Card>
      </div>
    )
  }

  if (error || !client) {
    return (
      <div>
        <PageHeader title="B2B client" />
        <Card section>
          <LoadErrorState message={error} onRetry={() => void load()} />
        </Card>
      </div>
    )
  }

  const isReady = roomCount > 0 && client.status === 'active'

  return (
    <div>
      <PageHeader
        title={client.name}
        subtitle={client.address}
        actions={
          <>
            <PillButton variant="secondary" onClick={() => setEditing(true)}>
              <Pencil size={14} /> Edit client
            </PillButton>
            <PillButton onClick={() => navigate(`/b2b/billing?client=${client.id}`)}>
              <DoorOpen size={14} /> Open billing
            </PillButton>
          </>
        }
      />

      <div style={{ marginBottom: '14px' }}>
        <ClientStatusBadge status={client.status} />
      </div>

      <StatRow>
        <StatCard label="Total rooms" value={String(roomCount)} />
        <StatCard
          label="Billing cadence"
          value={CYCLE_LABELS[client.billing_cycle_length] ?? client.billing_cycle_length}
          hint={openCycle ? `${openCycle.label} · open` : 'No cycle open yet'}
        />
        <StatCard
          label="Setup state"
          value={isReady ? 'Ready for billing' : 'Setup incomplete'}
          color={isReady ? '#15803D' : undefined}
          hint={roomCount === 0 ? 'Add rooms to start billing' : undefined}
        />
        <StatCard
          label="Open cycle value"
          value={openCycle ? formatMoney(openCycle.total_amount) : '-'}
          hint={openCycle ? `${openCycle.rooms_billed} of ${roomCount} rooms billed` : 'Nothing billed yet'}
        />
        {/* What this client still owes across every invoice ever issued - the
            figure an operator is looking for when they ask whether the client
            has paid. Amber only when there is something to chase. */}
        <StatCard
          label="Outstanding"
          value={formatMoney(outstanding?.amount ?? 0)}
          color={outstanding && outstanding.amount > 0 ? '#B45309' : undefined}
          hint={
            !outstanding || outstanding.invoice_count === 0
              ? 'Nothing due'
              : `${outstanding.invoice_count} unsettled invoice${outstanding.invoice_count === 1 ? '' : 's'}`
          }
        />
      </StatRow>

      <div style={{ display: 'grid', gap: '18px' }}>
        <Card section>
          <CardTitle>Configuration</CardTitle>
          {/* Field order follows the design's detail grid exactly. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0 40px' }}>
            <DetailRow label="Client type" value={TYPE_LABELS[client.client_type] ?? client.client_type} />
            <DetailRow label="Client ID" value={client.id} />
            <DetailRow
              label="Billing cycle"
              value={CYCLE_LABELS[client.billing_cycle_length] ?? client.billing_cycle_length}
            />
            <DetailRow label="Default rate card" value={rateCardSummary} />
            <DetailRow label="Pickup point" value={client.address} />
            <DetailRow label="Room configuration" value={client.room_pattern || `${roomCount} rooms`} />
            <DetailRow label="Point of contact" value={client.poc_name || '-'} />
            <DetailRow label="Contact" value={client.poc_phone || 'No phone on file'} />
          </div>

          <div
            style={{
              marginTop: '16px',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              padding: '12px 14px',
              borderRadius: '12px',
              background: colors.bgSecondary,
              border: `1px solid ${colors.border}`,
            }}
          >
            <CalendarDays size={16} color={colors.accentText} style={{ flexShrink: 0, marginTop: '1px' }} />
            <div>
              <div className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                Future billing cycles will use this configuration
              </div>
              <p className="font-body" style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textMuted }}>
                Rooms are created once and reused every cycle - they are never re-entered.
              </p>
            </div>
          </div>
        </Card>

        <Card section>
          <CardTitle
            right={<Chip tone="neutral">{roomCount} active</Chip>}
          >
            Room inventory
          </CardTitle>
          {rooms.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <EmptyState
                icon={DoorOpen}
                title="No rooms added yet"
                description="Generate the reusable room inventory for this client before a billing cycle begins."
              />
              <PillButton onClick={() => setEditing(true)}>Add rooms</PillButton>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {rooms.slice(0, 60).map((room) => (
                <Chip key={room.id} tone={room.is_active ? 'neutral' : 'warning'}>
                  {room.room_number}
                  {room.is_active ? null : ' · inactive'}
                </Chip>
              ))}
              {rooms.length > 60 ? <Chip tone="gold">+{rooms.length - 60} more</Chip> : null}
            </div>
          )}
        </Card>

        <Card section>
          <CardTitle>Invoice history</CardTitle>
          {/* Invoices arrive with the invoice engine; until then this states the
              fact rather than showing an empty table that looks like "none". */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <EmptyState
              icon={FileText}
              title="No invoices yet"
              description="Generate the first consolidated invoice at the end of this cycle."
            />
            {openCycle ? (
              <div
                className="font-body"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: colors.textMuted }}
              >
                <Check size={13} color="var(--chip-success-ink)" />
                {openCycle.label} is open with {formatMoney(openCycle.total_amount)} billed so far.
              </div>
            ) : null}
          </div>
        </Card>
      </div>

      {editing ? (
        <ClientFormModal
          client={client}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            void load()
          }}
        />
      ) : null}
    </div>
  )
}
